import React, { PureComponent } from 'react';
import Tippy from '@tippyjs/react';
import styled, { css } from 'styled-components';
import { Placement } from 'tippy.js';
import { withPopupEdge } from '@utils/popupUtils';

/**
 * Whether tooltips slide into place (Tippy's moveTransition) or simply appear there. On by default; a page can switch
 * it off for every tooltip inside it - the Task List does (Petr, 2026-10-20: "remove the slide in animation").
 */
export const TooltipSlideContext = React.createContext(true);

type Props = {
  target: React.ReactElement;
  content: any;
  preformattedText?: boolean;
  position?: Placement;
  width?: string;
  arrow?: boolean;
  lightTheme?: boolean;
  interactive?: boolean;
  hideOnClick?: boolean;
  trigger?: string;
  colorPicker?: boolean;
  customPicker?: boolean;
  visible?: boolean;
  controlled?: boolean;
  onClickOutside?: any;
  className?: any;
  lazy?: boolean;
  noPadding?: boolean;
  onCreate?: any;
  reference?: any;
  disabled?: boolean;
  popperPositionElement?: any;
  zIndex?: number;
  withoutDelay?: boolean;
  theme?: any;
  noTransition?: boolean;
  minWidth?: number;
  appendTo?: 'parent' | Element | ((ref: Element) => Element);
  onShow?: any;
  onHidden?: any;
  /** Tippy's [along, away] nudge from the target, when the usual place isn't right. */
  offset?: [number, number];
};

type State = { mounted: boolean };

export const DEFAULT_TIPPY_Z_INDEX = 9999;

/** Tippy's default distance between a trigger and its popup. */
export const POPUP_TRIGGER_GAP = 10;

/** Shared look and feel for the header's hover popups: a card that drops down out of its trigger. */
export const hoverPopupCss = css`
  max-width: unset !important;
  border-radius: 10px;
  box-shadow: 0 6px 25px ${props => props.theme.largeBoxShadow};

  /* open downwards out of the trigger rather than sliding in from the side */
  transform-origin: top center;
  &[data-state='hidden'] {
    opacity: 0;
    transform: translateY(-8px);
  }

  /*
   * Tippy leaves a gap between trigger and popup, and treats the cursor as having left once it is
   * more than interactiveBorder outside the popup, so moving down onto the popup closes it. This
   * strip makes the gap part of the popup itself, which widens the hover area downwards from the
   * trigger only -- raising interactiveBorder instead would widen it on every side, and the
   * neighbouring popup would stay open too.
   */
  &:before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    height: ${POPUP_TRIGGER_GAP}px;
  }
`;

class TippyTooltip extends PureComponent<Props, State> {
  static contextType = TooltipSlideContext;
  readonly state = { mounted: false };

  lazyPlugin = {
    fn: () => ({
      onShow: () => {
        // mimic onShown callback behaviour with the timeout - onShown is too slow
        setTimeout(() => this.setState({ mounted: true }), 1);
        if (this.props.onShow) {
          this.props.onShow();
        }
      },
      onHidden: () => {
        this.setState({ mounted: false });
        if (this.props.onHidden) {
          this.props.onHidden();
        }
      }
    })
  };

  render() {
    const {
      target,
      content,
      preformattedText,
      position = 'top',
      width,
      arrow = true,
      lightTheme,
      interactive = false,
      hideOnClick,
      trigger = 'mouseenter focus',
      colorPicker = false,
      visible,
      controlled = false,
      onClickOutside,
      className,
      lazy,
      appendTo,
      noPadding,
      onCreate,
      reference,
      popperPositionElement,
      zIndex,
      disabled,
      noTransition,
      minWidth,
      withoutDelay = false,
      theme,
      onShow,
      onHidden,
      offset
    } = this.props;
    const extraProps: any = onClickOutside ? { onClickOutside: onClickOutside } : {};
    if (!lazy) {
      if (onShow) {
        extraProps.onShow = onShow;
      }
      if (onHidden) {
        extraProps.onHidden = onHidden;
      }
    }
    if (popperPositionElement) {
      extraProps.getReferenceClientRect = () => popperPositionElement.getBoundingClientRect();
    }
    if (offset) extraProps.offset = offset;

    return (
      <Wrapper
        minWidth={minWidth}
        reference={reference}
        moveTransition={noTransition || !this.context ? undefined : 'transform 0.2s ease-out'}
        onCreate={onCreate || undefined}
        content={!lazy || this.state.mounted ? content : null}
        preformattedText={preformattedText}
        placement={position}
        maxWidth={width}
        disabled={disabled}
        delay={withoutDelay ? undefined : [200, 0]}
        plugins={lazy ? [this.lazyPlugin] : undefined}
        arrow={arrow}
        light={lightTheme}
        interactive={interactive}
        hideOnClick={hideOnClick}
        trigger={controlled ? undefined : trigger}
        appendTo={appendTo || document.getElementById('root') || document.body}
        colorPicker={colorPicker}
        visible={controlled ? visible : undefined}
        className={className}
        noPadding={noPadding}
        zIndex={zIndex}
        theme={theme}
        {...extraProps}
      >
        {target}
      </Wrapper>
    );
  }
}

export default TippyTooltip;

const Wrapper = styled(Tippy)<{
  preformattedText?: boolean;
  light?: boolean;
  minWidth?: number;
  colorPicker?: boolean;
  noPadding?: boolean;
}>`
  font-size: 12px;
  font-weight: 600;
  color: ${props => (props.light ? props.theme.textStrongColor : props.theme.background)};
  background-color: ${props => (props.light ? props.theme.background : props.theme.textStrongColor)};
  ${({ theme, light }) => {
    const shadow = `0 0 2px ${theme.boxShadow}, 0 2px 2px ${theme.boxShadow}`;
    // A light tippy is a popup in the page's colour, so it takes the dark edge too.
    return `box-shadow: ${light ? withPopupEdge(theme, shadow) : shadow};`;
  }};
  ${props => props.minWidth && `min-width:  ${props.minWidth}px;`};
  box-sizing: border-box;
  padding: ${props => (props.noPadding ? '0px' : '4px 8px')};
  ${({ preformattedText }) => preformattedText && 'white-space: pre;'}
  ${props =>
    props.colorPicker &&
    `> .tippy-content {
        padding: 5px 2px;
     }`};
  ${props =>
    props.noPadding &&
    `> .tippy-content {
        padding: 0px;
     }`};
  &[data-placement^='top'] > .tippy-arrow::before {
    border-top-color: ${({ theme }) => theme.textStrongColor};
  }
  &[data-placement^='bottom'] > .tippy-arrow::before {
    border-bottom-color: ${({ theme }) => theme.textStrongColor};
  }
  &[data-placement^='right'] > .tippy-arrow::before {
    border-right-color: ${({ theme }) => theme.textStrongColor};
  }
  &[data-placement^='left'] > .tippy-arrow::before {
    border-left-color: ${({ theme }) => theme.textStrongColor};
  }
`;
